import React, { Suspense } from "react";
import { Outlet, Route, Routes } from "react-router";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { ComponentPage } from "./ComponentsPage";
import { OrganizationView2 } from "./views/OrganizationView2/OrganizationView2";

const LoadableSelectOrganizationPage = lazyWithRetry(
	() =>
		import(
			"modules/selectOrganization/pages/SelectOrganization/SelectOrganizationPage"
		),
);

const LoadableDashboardPage = lazyWithRetry(() => import("modules/dashboard"));

const LoadableManageAttributesPage = lazyWithRetry(
	() => import("modules/organizationAttributes/pages/ManageAttributesPage"),
);

const LoadableAttributeDetailsPage = lazyWithRetry(
	() => import("modules/attributeDetails/pages/AttributeDetailsPage"),
);

const LoadableSurveyEditorPageV2 = lazyWithRetry(
	() => import("@/modules/surveyEditor2/SurveyEditorPage"),
);

const LoadableSurveyDetailsPage = lazyWithRetry(
	() => import("modules/surveyDetails/SurveyDetailsPage"),
);

const LoadableMembershipsPage = lazyWithRetry(
	() => import("modules/memberships/pages/Memberships/MembershipsPage"),
);
const LoadableMembershipDetailsPage = lazyWithRetry(
	() => import("modules/membershipDetails2/pages/MembershipDetailsPage"),
);
const LoadableMembershipRolesPage = lazyWithRetry(
	() => import("@/modules/membershipRoles"),
);

const LoadableReportsListPage = lazyWithRetry(
	() => import("modules/reportsList/pages/ReportsListPage"),
);
const LoadableReportPage = lazyWithRetry(
	() => import("modules/reports/page/ReportPage"),
);
const LoadableEditReportPage = lazyWithRetry(
	() => import("modules/reportEditor2/pages/EditReportPage"),
);

const LoadableSharedReportsAdminPage = lazyWithRetry(
	() => import("modules/sharedReports/SharedReportsAdmin"),
);

const LoadableManageSurveysPage = lazyWithRetry(
	() => import("@/modules/manageSurveys/ManageSurveysPage"),
);
const LoadableDeliveryPlansPage = lazyWithRetry(
	() => import("modules/deliveryPlans/DeliveryPlansList/DeliveryPlansListPage"),
);
const LoadableDeliveryPlanDetailsPage = lazyWithRetry(
	() =>
		import("modules/deliveryPlans/DeliveryPlanDetails/DeliveryPlanDetailsPage"),
);
const LoadableRoundDetailPage = lazyWithRetry(
	() => import("modules/deliveryPlans/RoundDetails/RoundDetailsPage"),
);

const LoadableDeliveryPlanEditPage = lazyWithRetry(
	() => import("modules/deliveryPlans/EditDeliveryPlan/DeliveryPlanEditPage"),
);

const LoadableOrganizationSettingsPage = lazyWithRetry(
	() => import("modules/organizationSettings/OrganizationSettingsPage"),
);

const LoadableLabelsPage = lazyWithRetry(
	() => import("modules/labels/pages/ManageLabelsPage"),
);

const LoadableCommentsPageV2 = lazyWithRetry(
	() => import("modules/comments/pages/CommentsPageV2"),
);

const LoadableFollowUpsPage = lazyWithRetry(
	() => import("modules/followups/pages/FollowUpsPage"),
);

const LoadableSharedContentPage = lazyWithRetry(
	() => import("modules/sharedContent/page/SharedContentPage"),
);

const LoadablePeerFeedback = lazyWithRetry(
	() => import("modules/peerFeedback/page/PeerFeedbackPage/PeerFeedbackPage"),
);

const LoadablePeerFeedbackDetails = lazyWithRetry(
	() =>
		import(
			"modules/peerFeedback/page/PeerFeedbackDetailsPage/PeerFeedbackDetailsPage"
		),
);

const LoadableBulkDistributionsListPage = lazyWithRetry(
	() => import("modules/bulkDistribution/BulkDistributionListPage"),
);

const LoadableBulkDistributionDetailsPage = lazyWithRetry(
	() => import("modules/bulkDistribution/BulkDistributionDetailsPage"),
);

const LoadableEditBulkDistributionPage = lazyWithRetry(
	() => import("modules/bulkDistribution/EditBulkDistributionPage"),
);

const LoadablePeerFeedbackTemplatesListPage = lazyWithRetry(
	() =>
		import("modules/peerFeedbackTemplates/pages/PeerFeedbackTemplatesListPage"),
);

const LoadablePeerFeedbackTemplateDetailsPage = lazyWithRetry(
	() =>
		import(
			"modules/peerFeedbackTemplates/pages/PeerFeedbackTemplateDetailsPage"
		),
);

const LoadableNotificationsPage = lazyWithRetry(
	() => import("modules/notifications/NotificationsPage"),
);

const LoadableOKRPage = lazyWithRetry(() => import("modules/okr/OKRPage"));

const WithOrganizationView: React.FC = () => {
	return (
		<OrganizationView2>
			<Suspense fallback={<div>Loading...</div>}>
				<Outlet />
			</Suspense>
		</OrganizationView2>
	);
};

export const AppRoutes = () => {
	return (
		<Routes>
			{/* <Route exact path="/" component={RedirectToLastOrganization} /> */}
			<Route
				index
				element={
					<Suspense fallback={<div>Loading...</div>}>
						<LoadableSelectOrganizationPage />
					</Suspense>
				}
			/>
			<Route path="orga/:organizationId" element={<WithOrganizationView />}>
				<Route index element={<LoadableDashboardPage />} />
				<Route path="notifications" element={<LoadableNotificationsPage />} />
				<Route path="reports">
					<Route index element={<LoadableReportsListPage />} />
					<Route path=":reportId" element={<LoadableReportPage />} />
				</Route>
				<Route path="manage-reports">
					<Route index element={<LoadableReportsListPage manage />} />
					<Route path=":reportId/edit" element={<LoadableEditReportPage />} />
				</Route>
				<Route path="attributes">
					<Route index element={<LoadableManageAttributesPage />} />
					<Route
						path=":attributeId"
						element={<LoadableAttributeDetailsPage />}
					/>
				</Route>
				<Route path="surveys">
					<Route index element={<LoadableManageSurveysPage />} />
					<Route path=":surveyId">
						<Route index element={<LoadableSurveyDetailsPage />} />

						<Route path="edit" element={<LoadableSurveyEditorPageV2 />} />
					</Route>
				</Route>
				<Route path="memberships">
					<Route index element={<LoadableMembershipsPage />} />
					<Route
						path=":membershipId"
						element={<LoadableMembershipDetailsPage />}
					/>
				</Route>
				<Route
					path="membership-roles"
					element={<LoadableMembershipRolesPage />}
				/>
				<Route
					path="shared-reports"
					element={<LoadableSharedReportsAdminPage />}
				/>
				<Route
					path="my-shared-reports"
					element={<LoadableSharedReportsAdminPage />}
				/>
				<Route path="delivery-plans">
					<Route index element={<LoadableDeliveryPlansPage />} />
					<Route path=":planId">
						<Route index element={<LoadableDeliveryPlanDetailsPage />} />
						<Route path="edit" element={<LoadableDeliveryPlanEditPage />} />
						<Route
							path="round/:roundId"
							element={<LoadableRoundDetailPage />}
						/>
					</Route>
				</Route>
				<Route path="settings" element={<LoadableOrganizationSettingsPage />} />
				<Route path="labels" element={<LoadableLabelsPage />} />
				<Route path="shared-content" element={<LoadableSharedContentPage />} />
				<Route path="comments" element={<LoadableCommentsPageV2 />} />
				<Route path="followups" element={<LoadableFollowUpsPage />} />
				<Route path="peer-feedback">
					<Route index element={<LoadablePeerFeedback />} />
					<Route
						path=":peerFeedbackId"
						element={<LoadablePeerFeedbackDetails />}
					/>
				</Route>
				<Route path="bulkDistributions">
					<Route index element={<LoadableBulkDistributionsListPage />} />
					<Route path=":bulkDistributionId">
						<Route index element={<LoadableBulkDistributionDetailsPage />} />
						<Route path="edit" element={<LoadableEditBulkDistributionPage />} />
					</Route>
				</Route>
				<Route path="peer-feedback-templates">
					<Route index element={<LoadablePeerFeedbackTemplatesListPage />} />
					<Route path=":peerFeedbackTemplateId">
						<Route
							index
							element={<LoadablePeerFeedbackTemplateDetailsPage />}
						/>
					</Route>
				</Route>
				<Route path="okr">
					<Route index element={<LoadableOKRPage />} />
				</Route>
			</Route>
			<Route path="components" element={<ComponentPage />} />
		</Routes>
	);
};
